import { Button, Typography } from '@mui/material';
import { useMemo } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import { updateDepositInfo } from '@/api/user';

import { FormErrorLogger } from '@/components/Form/FormErrorLogger';
import { RadioToggle } from '@/components/Form/RadioToggle';
import { TextInput } from '@/components/Form/TextInput';

import { useAppSelector } from '@/hooks/redux';
import { useMutationWithUserData } from '@/hooks/useMutationWithUserData';
import { useTranslate } from '@/hooks/useTranslate';

import { sortBankAccountsByProductAndCurrency } from '@/pages/account-type/utils/caymanAccount';

import { selectUserData } from '@/redux/user-data/selectors';

import { AccountFormData, UserAccountType } from '@/types/user';

import { getDataTestId } from '@/utils/browser';
import { getDepositInfoPayload } from '@/utils/user';

import { ActionBar } from '../../../../Layout';
import {
  isValidCaymanMonthlyDepositAmount,
  roundCaymanMonthlyDepositAmountOnBlur,
} from './QuestionAboutMonthlyDepositCayman/utils';
import { QuestionProps } from './QuestionDrawer.interface';
import { QuestionForm } from './QuestionForm';

import {
  FormFieldContainer,
  QuestionLabel,
  QuestionsContainerItem,
  QuestionsContainerItemYesNoContainer,
} from './QuestionAboutMultipleMonthlyDeposit.styled';

type FormData = {
  accounts: AccountFormData[];
};

type AccountSectionProps = Readonly<{
  account: UserAccountType;
  index: number;
  isPending: boolean;
}>;

const AccountSection = ({ account, index, isPending }: AccountSectionProps) => {
  const { t } = useTranslate();
  const {
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<FormData>();

  const yesNoOptions = [
    { value: true, label: t('questionDrawer.monthlyDeposit.button.yes') },
    { value: false, label: t('questionDrawer.monthlyDeposit.button.no') },
  ];

  const validateAmount = (value: unknown) => {
    const result = isValidCaymanMonthlyDepositAmount(value as string);
    return result === true ? true : t(result);
  };

  const handleAmountBlur =
    (
      index: number,
      field:
        | 'monthlyDepositAmount'
        | 'monthlyDepositsCount'
        | 'monthlyCashDepositAmount',
    ) =>
    () => {
      const fieldName = `accounts.${index}.${field}` as const;
      const raw = getValues(fieldName) || '';
      const rounded = roundCaymanMonthlyDepositAmountOnBlur(raw);
      if (rounded !== null) {
        setValue(fieldName, rounded, { shouldValidate: true });
      }
    };

  const monthlyDepositAmountRules = {
    required: t('questionDrawer.monthlyDeposit.monthlyDepositAmount.required'),
    validate: validateAmount,
  };

  const monthlyDepositsCountRules = {
    required: t('questionDrawer.monthlyDeposit.monthlyDepositsCount.required'),
    validate: validateAmount,
  };

  const monthlyCashDepositAmountRules = {
    required: t(
      'questionDrawer.monthlyDeposit.monthlyCashDepositAmount.required',
    ),
    validate: validateAmount,
  };

  const yesNoFieldRules = {
    required: t('questionDrawer.monthlyDeposit.sendReceiveMoney.required'),
  };

  const currency = account.currency;
  const cardTitle = `${currency} ${account.name}`;

  return (
    <QuestionsContainerItem key={account.id}>
      <Typography marginTop={2} marginBottom={2} variant="body1Medium">
        {cardTitle}
      </Typography>

      <FormFieldContainer>
        <TextInput
          type="text"
          control={control}
          name={`accounts.${index}.monthlyDepositAmount`}
          error={!!errors.accounts?.[index]?.monthlyDepositAmount}
          helperText={errors.accounts?.[index]?.monthlyDepositAmount?.message}
          rules={monthlyDepositAmountRules}
          placeholder={t(
            'questionDrawer.monthlyDeposit.monthlyDepositAmount.placeholder',
          )}
          label={t('questionDrawer.monthlyDeposit.monthlyDepositAmount.label')}
          inputMode="numeric"
          autoComplete="off"
          disabled={isPending}
          template="currency"
          startAdornment={currency}
          onBlur={handleAmountBlur(index, 'monthlyDepositAmount')}
        />
      </FormFieldContainer>

      <FormFieldContainer>
        <TextInput
          type="text"
          control={control}
          name={`accounts.${index}.monthlyDepositsCount`}
          error={!!errors.accounts?.[index]?.monthlyDepositsCount}
          helperText={errors.accounts?.[index]?.monthlyDepositsCount?.message}
          rules={monthlyDepositsCountRules}
          placeholder={t(
            'questionDrawer.monthlyDeposit.monthlyDepositsCount.placeholder',
          )}
          label={t('questionDrawer.monthlyDeposit.monthlyDepositsCount.label')}
          inputMode="numeric"
          autoComplete="off"
          disabled={isPending}
          onBlur={handleAmountBlur(index, 'monthlyDepositsCount')}
        />
      </FormFieldContainer>

      <FormFieldContainer>
        <TextInput
          type="text"
          control={control}
          name={`accounts.${index}.monthlyCashDepositAmount`}
          error={!!errors.accounts?.[index]?.monthlyCashDepositAmount}
          helperText={
            errors.accounts?.[index]?.monthlyCashDepositAmount?.message
          }
          rules={monthlyCashDepositAmountRules}
          placeholder={t(
            'questionDrawer.monthlyDeposit.monthlyCashDepositAmount.placeholder',
          )}
          label={t(
            'questionDrawer.monthlyDeposit.monthlyCashDepositAmount.label',
          )}
          inputMode="numeric"
          autoComplete="off"
          disabled={isPending}
          template="currency"
          startAdornment={currency}
          onBlur={handleAmountBlur(index, 'monthlyCashDepositAmount')}
        />
      </FormFieldContainer>

      <QuestionLabel>
        {t('questionDrawer.monthlyDeposit.sendReceiveMoney.label')}
      </QuestionLabel>
      <QuestionsContainerItemYesNoContainer>
        <RadioToggle
          control={control}
          name={`accounts.${index}.sendReceiveMoney`}
          options={yesNoOptions}
          rules={yesNoFieldRules}
        />
      </QuestionsContainerItemYesNoContainer>

      <QuestionLabel>
        {t('questionDrawer.monthlyDeposit.internationalWireTransfers.label')}
      </QuestionLabel>
      <QuestionsContainerItemYesNoContainer>
        <RadioToggle
          control={control}
          name={`accounts.${index}.internationalWireTransfers`}
          options={yesNoOptions}
          rules={{
            required: t(
              'questionDrawer.monthlyDeposit.internationalWireTransfers.required',
            ),
          }}
        />
      </QuestionsContainerItemYesNoContainer>
    </QuestionsContainerItem>
  );
};

export function QuestionAboutMultipleMonthlyDeposit({
  onAnswered,
  ...props
}: Readonly<QuestionProps>) {
  const { t } = useTranslate();
  const { user } = useAppSelector(selectUserData);

  const bankingAccounts = useMemo(
    () => sortBankAccountsByProductAndCurrency(user?.accountTypes || []),
    [user?.accountTypes],
  );

  const methods = useForm<FormData>({
    defaultValues: {
      accounts: bankingAccounts.map(() => ({
        monthlyDepositAmount: '',
        monthlyDepositsCount: '',
        monthlyCashDepositAmount: '',
      })),
    },
    mode: 'onChange',
  });
  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = methods;

  const { mutate, isPending, error } = useMutationWithUserData({
    mutationFn: updateDepositInfo,
    onFinished: onAnswered,
  });

  const onSubmit = handleSubmit(async payload => {
    if (!bankingAccounts.length) return;

    const depositInfoArray = getDepositInfoPayload(
      bankingAccounts,
      payload.accounts,
    );

    mutate(depositInfoArray);
  });

  return (
    <FormProvider {...methods}>
      <QuestionForm
        {...props}
        isLoading={isPending}
        answerError={error}
        onSubmit={onSubmit}
      >
        {bankingAccounts.map((account, index) => (
          <AccountSection
            account={account}
            index={index}
            key={account.id}
            isPending={isPending}
          />
        ))}
        <ActionBar>
          <Button
            {...getDataTestId(`basic-info-proceed-button`)}
            variant="outlined"
            fullWidth
            type="submit"
            disabled={!isValid || isPending}
          >
            {t('questionDrawer.monthlyDeposit.button.proceed')}
          </Button>
        </ActionBar>
        <FormErrorLogger
          formName="QuestionAboutMonthlyDepositCayman"
          control={control}
        />
      </QuestionForm>
    </FormProvider>
  );
}
